/**
 * `useMfaRefusalText()` / `mfaRefusalText()` — the ONE sentence an MFA form shows for the refusals a SIGNED-IN
 * person's second-factor request can meet whatever form it came from (the table below names each by its code):
 *
 * - their typed password or code is wrong (N139, below);
 * - the change could not be recorded (ECOSYSTEM-EMAIL-001 Q-EE-22): every API answers a set-up, turn-off, backup-code
 *   regeneration or step-up whose audit record could be neither written nor kept with 503
 *   `AUDIT_RECORD_UNAVAILABLE` and changes nothing, so the person is told, in the ruled words ("Plain, retry"), that
 *   trying again is safe.
 *
 * Every API answers that class with ONE refusal: HTTP 403 `CREDENTIAL_INCORRECT` (`@puffin-core/response-helpers`
 * `sendCredentialIncorrect`) — MFA enrolment's password and first code, backup-code regeneration, MFA disable, the
 * in-session step-up. Not a 401: every session client reads a 401 as "the access token expired" and re-sends the
 * request, so a wrong code was judged twice. The English `message` beside the code is for logs; a person sees THIS.
 *
 * This package owns every MFA sentence, so the MFA forms word the refusal here; `@puffin-core/auth-shell`'s
 * `useCredentialRefusalText` words it on the password and PIN forms. Both key on the same code, and the password
 * sentence is the same words in both corpora (`credentialRecheckRefusal.contract` holds them equal).
 *
 * @module @puffin-core/mfa-ui/hooks/useMfaRefusalText
 */

import { useCallback } from 'react';

import { useMfaTranslate } from './useMfaTranslate.js';

/** The wire code of the ONE signed-in re-check refusal (`ERROR_CODES.CREDENTIAL_INCORRECT` on every API). */
const CREDENTIAL_INCORRECT_CODE = 'CREDENTIAL_INCORRECT';

/**
 * The wire code of a second-factor change refused because its record could not be kept (`@puffin-core/auth-routes`
 * `AUDIT_RECORD_UNAVAILABLE_CODE`, Q-EE-22). Restated here: this package is the browser tier and depends on no API
 * package. (The enrolment codes are not restated: they live in the data-only `@puffin-core/factor-enrollment-codes`.)
 */
const AUDIT_RECORD_UNAVAILABLE_CODE = 'AUDIT_RECORD_UNAVAILABLE';

/**
 * The wire code of a passkey ceremony refused because its challenge store could not answer (`@puffin-core/passkey`
 * `PASSKEY_CHALLENGE_STORE_UNAVAILABLE`, a 503; ECOSYSTEM-EMAIL-001 Q-EE-41). Restated for the same reason. The ONE
 * constant: the passkey card words a registration refusal by it too.
 */
export const PASSKEY_CHALLENGE_STORE_UNAVAILABLE_CODE = 'PASSKEY_CHALLENGE_STORE_UNAVAILABLE';

/**
 * Each refusal worded the same whatever secret the form asked for, by its code → its `mfa` leaf: a change refused for
 * its record (Q-EE-22), a passkey ceremony whose store could not answer (Q-EE-41), and the set-up / backup-code routes'
 * own refusals (`@puffin-core/auth-routes` `platformMfaSetup` and its tenant twin; ErrorState Unit B): already set up
 * (409 `MFA_ALREADY_ENABLED`), a set-up whose pending secret is gone (400 `MFA_NO_PENDING_SECRET`), no second factor to
 * regenerate codes for (400 `MFA_NOT_ENABLED`), and too many attempts (429 `RATE_LIMIT_EXCEEDED`). Before this table,
 * the last four reached every form as the server's English sentence.
 */
const SECRET_INDEPENDENT_REFUSAL_LEAVES = Object.freeze({
  [AUDIT_RECORD_UNAVAILABLE_CODE]: 'error_change_not_recorded',
  [PASSKEY_CHALLENGE_STORE_UNAVAILABLE_CODE]: 'error_passkey_unavailable',
  MFA_ALREADY_ENABLED: 'error_already_enabled',
  MFA_NO_PENDING_SECRET: 'error_setup_expired',
  MFA_NOT_ENABLED: 'error_not_enabled',
  RATE_LIMIT_EXCEEDED: 'error_rate_limited',
});

/** The secrets an MFA form asks for, each naming its leaf. */
export const MFA_REFUSAL_KINDS = Object.freeze(['password', 'code']);

/**
 * Word a refusal through a translator — the pure half, for a caller that already holds one (the step-up dialog).
 *
 * @param {{errorCode?: string}|null|undefined} error - an API failure in the clients' shape (`errorCode`)
 * @param {'password'|'code'} kind - the secret the form asked for
 * @param {(key: string) => string} str - the `mfa` corpus translator
 * @returns {string|null} the sentence, or `null` when it is neither refusal
 * @throws {TypeError} when `kind` names a secret this package does not word (a programming error)
 */
export function mfaRefusalText(error, kind, str) {
  if (!MFA_REFUSAL_KINDS.includes(kind)) {
    throw new TypeError(`mfaRefusalText: kind must be one of ${MFA_REFUSAL_KINDS.join(', ')}`);
  }
  if (!error) return null;
  // An OWN key only: an inherited name (`constructor`) is never a refusal this package words.
  if (Object.hasOwn(SECRET_INDEPENDENT_REFUSAL_LEAVES, error.errorCode ?? '')) {
    return str(SECRET_INDEPENDENT_REFUSAL_LEAVES[error.errorCode]);
  }
  if (error.errorCode !== CREDENTIAL_INCORRECT_CODE) return null;
  return str(`error_${kind}_incorrect`);
}

/**
 * @returns {(error: {errorCode?: string}|null|undefined, kind: 'password'|'code') => string|null}
 */
export function useMfaRefusalText() {
  const str = useMfaTranslate();
  return useCallback((error, kind) => mfaRefusalText(error, kind, str), [str]);
}

export default useMfaRefusalText;
